import { useState, useCallback, useRef } from 'react'
import { apiClient } from '@/lib/api/client'
import { toast } from 'sonner'

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
const MAX_IMAGE_SIZE = 5 * 1024 * 1024 // 5MB

export type ImageUploadStatus = 'idle' | 'validating' | 'uploading' | 'done' | 'error'

export interface ImageUploadState {
  status: ImageUploadStatus
  progress: number
  error?: string
  imageUrl?: string
}

export function useImageUpload(agentId: string, draftId: string) {
  const [state, setState] = useState<ImageUploadState>({ status: 'idle', progress: 0 })
  const xhrRef = useRef<XMLHttpRequest | null>(null)

  const validateFile = useCallback((file: File): string | null => {
    if (!IMAGE_MIME_TYPES.includes(file.type as typeof IMAGE_MIME_TYPES[number])) {
      return 'Only JPEG, PNG, and WebP files are supported.'
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return 'File exceeds 5 MB limit.'
    }
    return null
  }, [])

  const upload = useCallback(async (file: File): Promise<string | null> => {
    setState({ status: 'validating', progress: 0 })

    const validationError = validateFile(file)
    if (validationError) {
      toast.error(validationError)
      setState({ status: 'error', progress: 0, error: validationError })
      return null
    }

    try {
      setState({ status: 'uploading', progress: 0 })

      // Step 1: Get presigned URL
      const { imageUrl, uploadUrl } = await apiClient<{ imageUrl: string; uploadUrl: string }>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/images/upload-url`,
        {
          method: 'POST',
          body: JSON.stringify({ fileName: file.name, mimeType: file.type, sizeBytes: file.size }),
        },
      )

      // Step 2: Upload to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhrRef.current = xhr

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            setState((prev) => ({ ...prev, progress: percent }))
          }
        }

        xhr.onload = () => {
          xhrRef.current = null
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(`Upload failed with status ${xhr.status}`))
        }

        xhr.onerror = () => {
          xhrRef.current = null
          reject(new Error('Network error during upload'))
        }

        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })

      setState({ status: 'done', progress: 100, imageUrl })
      return imageUrl
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Check your file and try again.'
      toast.error(message)
      setState({ status: 'error', progress: 0, error: message })
      return null
    }
  }, [agentId, draftId, validateFile])

  const reset = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort()
      xhrRef.current = null
    }
    setState({ status: 'idle', progress: 0 })
  }, [])

  return { state, upload, reset, validateFile }
}
